import { useState, useEffect, useCallback, useRef } from 'react';
import { Check, TriangleAlert as AlertTriangle, X, Loader as Loader2, Users, CircleAlert as AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';

interface Upload {
  id: string;
  total_rows: number;
  processed_rows: number;
  failed_rows: number;
  skipped_rows: number;
  status: string;
  error_message: string;
  created_at: string;
}

interface RowSummary {
  completed: number;
  not_found: number;
  error: number;
  team_request_sent: number;
  enriching: number;
  pending: number;
}

interface ProgressResponse {
  upload: Upload | null;
  row_summary: RowSummary | null;
}

export default function BulkUploadProgressBar() {
  const { apiGet, apiPost, apiDelete } = useApi();
  const { user, organisation } = useAuth();
  const [upload, setUpload] = useState<Upload | null>(null);
  const [rowSummary, setRowSummary] = useState<RowSummary | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<{ completed: number; failed: number } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  const loadUpload = useCallback(async () => {
    if (!user || !organisation) return;

    let data: Upload | null = null;
    let summary: RowSummary | null = null;
    try {
      const res = await apiGet<ProgressResponse>(
        `/v1/bulk-account-uploads/progress?org_id=${encodeURIComponent(organisation.id)}`,
      );
      data = res.upload;
      summary = res.row_summary;
    } catch {
      return;
    }

    if (!data) { setUpload(null); return; }

    const age = Date.now() - new Date(data.created_at).getTime();
    // Safety net: never show any upload older than 3h, even a non-terminal one. The
    // server-side reconcile sweep normally fails a stuck upload within ~30 min, but if
    // that ever misfires this stops the banner from hanging forever.
    if (age > 3 * 60 * 60 * 1000) {
      setUpload(null);
      return;
    }
    // Only show terminal uploads from the last 2 hours.
    if (age > 2 * 60 * 60 * 1000 && ['completed', 'error'].includes(data.status)) {
      setUpload(null);
      return;
    }

    setUpload(data);
    if (summary) setRowSummary(summary);
  }, [apiGet, user, organisation]);

  useEffect(() => {
    loadUpload();
    pollRef.current = setInterval(loadUpload, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadUpload]);

  // Stop polling once terminal
  useEffect(() => {
    if (upload && ['completed', 'error'].includes(upload.status) && pollRef.current) {
      clearInterval(pollRef.current);
    }
  }, [upload?.status]);

  const handleCancel = async () => {
    if (!upload || cancelling) return;
    setCancelling(true);
    try {
      await apiDelete(`/v1/bulk-account-uploads/${upload.id}`);
    } catch {
      // Best effort: hide the banner regardless so the user is never stuck with it.
    } finally {
      if (pollRef.current) clearInterval(pollRef.current);
      setCancelling(false);
      setUpload(null);
      setDismissed(true);
    }
  };

  const handleRetry = async () => {
    if (!organisation || retrying) return;
    setRetrying(true);
    setRetryResult(null);
    try {
      const data = await apiPost<{ completed?: number; failed?: number }>('/v1/retry-failed-accounts', { org_id: organisation.id });
      setRetryResult({ completed: data.completed ?? 0, failed: data.failed ?? 0 });
      await loadUpload();
    } catch {
      setRetryResult({ completed: 0, failed: 0 });
    } finally {
      setRetrying(false);
    }
  };

  if (!upload || dismissed) return null;

  const isTerminal = upload.status === 'completed' || upload.status === 'error';
  const total = upload.total_rows || 1;
  const processed = upload.processed_rows;
  const pct = Math.round((processed / total) * 100);

  // Estimate remaining time
  const elapsed = (Date.now() - new Date(upload.created_at).getTime()) / 1000;
  const perAccount = processed > 0 ? elapsed / processed : 90;
  const remaining = Math.max(0, Math.round((total - processed) * perAccount));
  const remainingMin = Math.floor(remaining / 60);
  const remainingSec = remaining % 60;

  const statusColor = upload.status === 'error' ? 'bg-app-critical'
    : upload.status === 'completed' && upload.failed_rows > 0 ? 'bg-app-warning'
    : upload.status === 'completed' ? 'bg-app-positive'
    : 'bg-app-accent';

  const bgColor = upload.status === 'error' ? 'bg-app-critical/10 border-app-critical'
    : upload.status === 'completed' && upload.failed_rows > 0 ? 'bg-app-warning/10 border-app-warning'
    : upload.status === 'completed' ? 'bg-app-positive/10 border-app-positive'
    : 'bg-app-surface border-app-line';

  return (
    <div className={`mb-6 p-4 border rounded-lg ${bgColor} transition-colors duration-500`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {upload.status === 'processing' && <Loader2 className="w-4 h-4 text-forest-600 animate-spin" />}
          {upload.status === 'tiering' && <Loader2 className="w-4 h-4 text-app-focus animate-spin" />}
          {upload.status === 'completed' && upload.failed_rows === 0 && <Check className="w-4 h-4 text-app-positive" />}
          {upload.status === 'completed' && upload.failed_rows > 0 && <AlertTriangle className="w-4 h-4 text-app-warning" />}
          {upload.status === 'error' && <AlertCircle className="w-4 h-4 text-app-critical" />}

          <span className="text-sm font-medium text-app-ink">
            {upload.status === 'validating' && 'Preparing bulk upload...'}
            {upload.status === 'processing' && `Enriching accounts: ${processed} of ${total} processed`}
            {upload.status === 'tiering' && 'Tiering accounts...'}
            {upload.status === 'completed' && 'Bulk upload complete'}
            {upload.status === 'error' && 'Bulk upload failed'}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {!isTerminal && remaining > 0 && processed > 0 && (
            <span className="text-xs text-app-muted">
              ~{remainingMin > 0 ? `${remainingMin}m ` : ''}{remainingSec}s remaining
            </span>
          )}
          {!isTerminal && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-app-muted hover:text-app-critical hover:bg-app-critical/10 transition-colors disabled:opacity-50"
            >
              {cancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
              {cancelling ? 'Cancelling...' : 'Cancel'}
            </button>
          )}
          {isTerminal && (
            <button onClick={() => setDismissed(true)} className="p-1 text-app-muted hover:text-app-muted rounded">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {!isTerminal && (
        <div className="w-full h-2 bg-app-line rounded-full overflow-hidden mb-2" role="progressbar" aria-label="Bulk account upload" aria-valuemin={0} aria-valuemax={100} aria-valuenow={upload.status === 'tiering' ? 100 : Math.round(pct)}>
          <div
            className={`h-full rounded-full transition duration-700 ease-out ${statusColor}`}
            style={{ width: `${upload.status === 'tiering' ? 100 : Math.max(pct, 2)}%` }}
          />
        </div>
      )}

      {/* Summary stats for completed */}
      {isTerminal && rowSummary && (
        <div className="flex items-center gap-4 mt-1 text-xs">
          {rowSummary.completed > 0 && (
            <span className="flex items-center gap-1 text-app-positive">
              <Check className="w-3 h-3" /> {rowSummary.completed} enriched
            </span>
          )}
          {rowSummary.error > 0 && (
            <span className="flex items-center gap-2 text-app-critical">
              <AlertTriangle className="w-3 h-3" />
              {rowSummary.error} failed
              <button
                onClick={handleRetry}
                disabled={retrying}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-app-critical/10 hover:bg-app-critical text-app-critical font-medium transition-colors disabled:opacity-50"
              >
                {retrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {retrying ? 'Retrying...' : 'Retry Failed'}
              </button>
              {retryResult && !retrying && (
                <span className="text-app-positive">{retryResult.completed} recovered</span>
              )}
            </span>
          )}
          {rowSummary.not_found > 0 && (
            <span className="flex items-center gap-1 text-app-warning">
              <AlertTriangle className="w-3 h-3" /> {rowSummary.not_found} not found. Delete and re-add with the correct details.
            </span>
          )}
          {rowSummary.team_request_sent > 0 && (
            <span className="flex items-center gap-1 text-app-focus">
              <Users className="w-3 h-3" /> {rowSummary.team_request_sent} join requests sent
            </span>
          )}
          {upload.skipped_rows > 0 && (
            <span className="text-app-muted">{upload.skipped_rows} skipped</span>
          )}
        </div>
      )}

      {upload.status === 'error' && upload.error_message && (
        <p className="text-xs text-app-critical mt-1">{upload.error_message}</p>
      )}
    </div>
  );
}
